import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  getLightingNorms, getLightingRecommendation, getLightingReport, getLightingStf
} from '../lib/api';
import {
  Lightbulb, Sun, Ruler, Zap, Eye, ShieldCheck, ShieldX,
  Download, FileText, Building2, Loader2, Sparkles, ChevronRight,
  BarChart3, ArrowLeft, Palette, DollarSign, Info
} from 'lucide-react';

const t = (lang, pl, en) => lang === 'pl' ? pl : en;

const ROOM_TYPES = [
  { key: 'biuro_ogolne', pl: 'Biuro ogolne', en: 'General Office', lux: 500 },
  { key: 'biuro_open_space', pl: 'Open Space', en: 'Open Space', lux: 500 },
  { key: 'sala_konferencyjna', pl: 'Sala konferencyjna', en: 'Conference Room', lux: 500 },
  { key: 'recepcja', pl: 'Recepcja / Hol', en: 'Reception / Lobby', lux: 300 },
  { key: 'korytarz', pl: 'Korytarz', en: 'Corridor', lux: 100 },
  { key: 'toaleta', pl: 'Toaleta', en: 'Restroom', lux: 200 },
  { key: 'klatka_schodowa', pl: 'Klatka schodowa', en: 'Staircase', lux: 150 },
  { key: 'magazyn', pl: 'Magazyn', en: 'Warehouse', lux: 200 },
  { key: 'hala_produkcyjna', pl: 'Hala produkcyjna', en: 'Production Hall', lux: 300 },
  { key: 'sklep_ogolny', pl: 'Sklep', en: 'Shop', lux: 300 },
  { key: 'sklep_moda', pl: 'Sklep z moda', en: 'Fashion Store', lux: 500 },
  { key: 'sklep_spozywczy', pl: 'Sklep spozywczy', en: 'Grocery Store', lux: 300 },
  { key: 'restauracja', pl: 'Restauracja', en: 'Restaurant', lux: 200 },
  { key: 'kawiarnia', pl: 'Kawiarnia', en: 'Cafe', lux: 200 },
  { key: 'bar', pl: 'Bar', en: 'Bar', lux: 100 },
  { key: 'hotel_pokoj', pl: 'Pokoj hotelowy', en: 'Hotel Room', lux: 300 },
  { key: 'hotel_lobby', pl: 'Lobby hotelowe', en: 'Hotel Lobby', lux: 300 },
  { key: 'hotel_spa', pl: 'SPA / Wellness', en: 'SPA / Wellness', lux: 200 },
  { key: 'szpital_gabinet', pl: 'Gabinet lekarski', en: 'Medical Office', lux: 500 },
  { key: 'szpital_korytarz', pl: 'Korytarz szpitalny', en: 'Hospital Corridor', lux: 200 },
  { key: 'szkola_klasa', pl: 'Sala lekcyjna', en: 'Classroom', lux: 500 },
  { key: 'szkola_aula', pl: 'Aula / Wykladowa', en: 'Lecture Hall', lux: 500 },
  { key: 'muzeum', pl: 'Muzeum / Galeria', en: 'Museum / Gallery', lux: 300 },
  { key: 'parking', pl: 'Parking podziemny', en: 'Underground Parking', lux: 75 },
  { key: 'fasada', pl: 'Fasada budynku', en: 'Building Facade', lux: 0 },
  { key: 'ogrod', pl: 'Ogrod / Teren zielony', en: 'Garden / Landscape', lux: 0 },
];

const STYLES = [
  { key: 'modern', pl: 'Nowoczesny', en: 'Modern' },
  { key: 'classic', pl: 'Klasyczny', en: 'Classic' },
  { key: 'industrial', pl: 'Industrialny', en: 'Industrial' },
  { key: 'minimal', pl: 'Minimalistyczny', en: 'Minimalist' },
  { key: 'decorative', pl: 'Dekoracyjny', en: 'Decorative' },
];

export default function LightingAdvisor({ lang, tenantId }) {
  var [step, setStep] = useState('form'); // form | loading | result
  var [form, setForm] = useState({
    room_type: 'biuro_ogolne',
    length_m: 10,
    width_m: 8,
    height_m: 2.8,
    style: 'modern',
    budget_pln: '',
    preferences: '',
    client_name: '',
    project_name: '',
  });
  var [result, setResult] = useState(null);
  var [downloading, setDownloading] = useState(null);

  var area = (form.length_m * form.width_m) || 0;
  var selectedRoom = ROOM_TYPES.find(r => r.key === form.room_type) || ROOM_TYPES[0];

  function updateField(field, value) {
    setForm(function(prev) { return { ...prev, [field]: value }; });
  }

  async function handleSubmit() {
    if (!form.length_m || !form.width_m || !form.height_m) {
      toast.error(t(lang, 'Podaj wymiary pomieszczenia', 'Enter room dimensions'));
      return;
    }
    setStep('loading');
    try {
      var payload = {
        room_type: form.room_type,
        area_m2: area,
        height_m: parseFloat(form.height_m),
        length_m: parseFloat(form.length_m),
        width_m: parseFloat(form.width_m),
        style: form.style,
        budget_pln: form.budget_pln ? parseFloat(form.budget_pln) : null,
        preferences: form.preferences || '',
        client_name: form.client_name || '',
        project_name: form.project_name || '',
      };
      var data = await getLightingRecommendation(payload, tenantId);
      setResult(data);
      setStep('result');
      toast.success(t(lang, 'Rekomendacje gotowe!', 'Recommendations ready!'));
    } catch (err) {
      console.error(err);
      toast.error(t(lang, 'Blad analizy: ' + (err.response?.data?.detail || err.message), 'Analysis error: ' + (err.response?.data?.detail || err.message)));
      setStep('form');
    }
  }

  async function handleDownloadPdf() {
    if (!result) return;
    setDownloading('pdf');
    try {
      var reportData = {
        name: form.project_name || t(lang, 'Projekt oswietlenia', 'Lighting Project'),
        client: form.client_name || '',
        address: '',
        rooms: (result.rooms || []).map(function(r) {
          return {
            ...r,
            length_m: parseFloat(form.length_m),
            width_m: parseFloat(form.width_m),
            height_m: parseFloat(form.height_m),
          };
        }),
      };
      var blob = await getLightingReport(reportData, tenantId);
      var url = window.URL.createObjectURL(new Blob([blob]));
      var a = document.createElement('a');
      a.href = url;
      a.download = 'projekt_oswietlenia.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t(lang, 'PDF pobrany', 'PDF downloaded'));
    } catch (err) {
      toast.error(t(lang, 'Blad generowania PDF', 'PDF generation error'));
    }
    setDownloading(null);
  }

  async function handleDownloadStf() {
    if (!result) return;
    setDownloading('stf');
    try {
      var stfData = {
        project_name: form.project_name || 'Lighting Project',
        rooms: (result.rooms || []).map(function(r) {
          return {
            name: r.room?.name || r.name || form.room_type,
            length_m: parseFloat(form.length_m),
            width_m: parseFloat(form.width_m),
            height_m: parseFloat(form.height_m),
            luminaires: (r.luminaires_used || r.recommendations || []).map(function(lum) {
              return {
                name: lum.name || 'Oprawa',
                manufacturer: lum.manufacturer || '',
                quantity: lum.quantity || 1,
                power_w: lum.power_w || lum.power || 40,
                lumens: lum.lumens || 4000,
              };
            }),
          };
        }),
      };
      var text = await getLightingStf(stfData, tenantId);
      var blob2 = new Blob([text], { type: 'text/plain' });
      var url2 = window.URL.createObjectURL(blob2);
      var a2 = document.createElement('a');
      a2.href = url2;
      a2.download = 'projekt.stf';
      a2.click();
      window.URL.revokeObjectURL(url2);
      toast.success(t(lang, 'STF pobrany', 'STF downloaded'));
    } catch (err) {
      toast.error(t(lang, 'Blad generowania STF', 'STF generation error'));
    }
    setDownloading(null);
  }

  // ==== FORM VIEW ====
  if (step === 'form') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold">{t(lang, 'Doradca Oswietlenia', 'Lighting Advisor')}</h1>
            <p className="text-sm text-muted-foreground">
              {t(lang, 'Dobor lamp wg PN-EN 12464-1 — zastap architekta', 'Lamp selection per PN-EN 12464-1 — replace the architect')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-2 space-y-4">
            {/* Project Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {t(lang, 'Dane projektu', 'Project Info')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">{t(lang, 'Nazwa projektu', 'Project Name')}</label>
                    <Input value={form.project_name} onChange={e => updateField('project_name', e.target.value)}
                      placeholder={t(lang, 'np. Biuro ABC', 'e.g. Office ABC')} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">{t(lang, 'Klient', 'Client')}</label>
                    <Input value={form.client_name} onChange={e => updateField('client_name', e.target.value)}
                      placeholder={t(lang, 'np. Jan Kowalski', 'e.g. John Smith')} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Room Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Sun className="w-4 h-4" />
                  {t(lang, 'Typ pomieszczenia', 'Room Type')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {ROOM_TYPES.map(function(room) {
                    var isSelected = form.room_type === room.key;
                    return (
                      <button
                        key={room.key}
                        onClick={function() { updateField('room_type', room.key); }}
                        className={`p-2 rounded-lg border text-left text-xs transition-all ${
                          isSelected
                            ? 'border-amber-500 bg-amber-500/10 text-amber-500 font-medium'
                            : 'border-border hover:border-amber-500/30 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <div>{lang === 'pl' ? room.pl : room.en}</div>
                        {room.lux > 0 && <div className="text-[10px] opacity-60">{room.lux} lux</div>}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Dimensions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Ruler className="w-4 h-4" />
                  {t(lang, 'Wymiary pomieszczenia', 'Room Dimensions')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">{t(lang, 'Dlugosc (m)', 'Length (m)')}</label>
                    <Input type="number" step="0.1" value={form.length_m}
                      onChange={e => updateField('length_m', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">{t(lang, 'Szerokosc (m)', 'Width (m)')}</label>
                    <Input type="number" step="0.1" value={form.width_m}
                      onChange={e => updateField('width_m', parseFloat(e.target.value) || 0)} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">{t(lang, 'Wysokosc (m)', 'Height (m)')}</label>
                    <Input type="number" step="0.1" value={form.height_m}
                      onChange={e => updateField('height_m', parseFloat(e.target.value) || 0)} />
                  </div>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {t(lang, 'Powierzchnia', 'Area')}: <span className="font-medium text-foreground">{area.toFixed(1)} m²</span>
                </div>
              </CardContent>
            </Card>

            {/* Style + Budget */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  {t(lang, 'Styl i budzet', 'Style & Budget')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground">{t(lang, 'Styl', 'Style')}</label>
                  <div className="flex gap-2 mt-1">
                    {STYLES.map(function(s) {
                      return (
                        <button
                          key={s.key}
                          onClick={function() { updateField('style', s.key); }}
                          className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                            form.style === s.key
                              ? 'border-amber-500 bg-amber-500/10 text-amber-500 font-medium'
                              : 'border-border text-muted-foreground hover:border-amber-500/30'
                          }`}
                        >
                          {lang === 'pl' ? s.pl : s.en}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">{t(lang, 'Budzet (PLN, opcjonalny)', 'Budget (PLN, optional)')}</label>
                    <Input type="number" value={form.budget_pln}
                      onChange={e => updateField('budget_pln', e.target.value)}
                      placeholder="np. 15000" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">{t(lang, 'Preferencje (opcjonalne)', 'Preferences (optional)')}</label>
                    <Input value={form.preferences}
                      onChange={e => updateField('preferences', e.target.value)}
                      placeholder={t(lang, 'np. cieple, sciemniane', 'e.g. warm, dimmable')} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Summary + Action */}
          <div className="space-y-4">
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4 text-amber-500" />
                  {t(lang, 'Podsumowanie', 'Summary')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t(lang, 'Pomieszczenie', 'Room')}</span>
                  <span className="font-medium">{lang === 'pl' ? selectedRoom.pl : selectedRoom.en}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t(lang, 'Powierzchnia', 'Area')}</span>
                  <span className="font-medium">{area.toFixed(1)} m²</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t(lang, 'Wysokosc', 'Height')}</span>
                  <span className="font-medium">{form.height_m} m</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t(lang, 'Wymagane lux', 'Required lux')}</span>
                  <span className="font-medium text-amber-500">{selectedRoom.lux} lx</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t(lang, 'Styl', 'Style')}</span>
                  <span className="font-medium">{(STYLES.find(s => s.key === form.style) || STYLES[0])[lang === 'pl' ? 'pl' : 'en']}</span>
                </div>
                {form.budget_pln && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t(lang, 'Budzet', 'Budget')}</span>
                    <span className="font-medium">{Number(form.budget_pln).toLocaleString()} PLN</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button
              onClick={handleSubmit}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              size="lg"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {t(lang, 'Dobierz oswietlenie', 'Select Lighting')}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>

            <div className="text-xs text-muted-foreground text-center">
              {t(lang,
                'Analiza wg normy PN-EN 12464-1. Rekomendacje z bazy 36k+ produktow.',
                'Analysis per PN-EN 12464-1. Recommendations from 36k+ products database.'
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==== LOADING VIEW ====
  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto animate-pulse">
            <Lightbulb className="w-8 h-8 text-amber-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold">{t(lang, 'Analizuje pomieszczenie...', 'Analyzing room...')}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t(lang,
                'Dobieramy lampy z bazy 36k+ produktow wg normy PN-EN 12464-1',
                'Selecting lamps from 36k+ products per PN-EN 12464-1'
              )}
            </p>
          </div>
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" />
        </div>
      </div>
    );
  }

  // ==== RESULT VIEW ====
  var rooms = result?.rooms || [];
  var overallCompliance = result?.overall_compliance;
  var recommendations = result?.recommendations || [];
  var normReqs = result?.norm_requirements || {};
  var calcNeeds = result?.calculated_needs || {};
  var firstRoom = rooms[0] || {};
  var compliance = firstRoom.compliance || result?.compliance || {};
  var calculation = firstRoom.calculation || firstRoom.results || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={function() { setStep('form'); setResult(null); }}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t(lang, 'Nowy projekt', 'New Project')}
          </Button>
          <div>
            <h1 className="text-xl font-bold">{form.project_name || t(lang, 'Wynik analizy', 'Analysis Result')}</h1>
            <p className="text-sm text-muted-foreground">
              {lang === 'pl' ? selectedRoom.pl : selectedRoom.en} — {area.toFixed(1)} m²
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloading === 'pdf'}>
            {downloading === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
            {t(lang, 'Raport PDF', 'PDF Report')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadStf} disabled={downloading === 'stf'}>
            {downloading === 'stf' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            DIALux STF
          </Button>
        </div>
      </div>

      {/* Compliance Banner */}
      <Card className={overallCompliance || compliance.meets_norm
        ? 'border-green-500/30 bg-green-500/5'
        : 'border-red-500/30 bg-red-500/5'
      }>
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            {overallCompliance || compliance.meets_norm
              ? <ShieldCheck className="w-6 h-6 text-green-500" />
              : <ShieldX className="w-6 h-6 text-red-500" />
            }
            <div>
              <div className="font-bold">
                {overallCompliance || compliance.meets_norm
                  ? t(lang, 'ZGODNY z PN-EN 12464-1', 'COMPLIANT with PN-EN 12464-1')
                  : t(lang, 'NIEZGODNY z PN-EN 12464-1', 'NON-COMPLIANT with PN-EN 12464-1')
                }
              </div>
              {compliance.issues && compliance.issues.length > 0 && (
                <div className="text-sm text-muted-foreground mt-1">
                  {compliance.issues.join(' | ')}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Calculations */}
        <div className="lg:col-span-2 space-y-4">
          {/* Norm Requirements */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                {t(lang, 'Wymagania normy i obliczenia', 'Norm Requirements & Calculations')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <Sun className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                  <div className="text-lg font-bold">
                    {calculation.e_average_lux?.toFixed(0) || normReqs.lux || '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t(lang, 'Srednie lux', 'Average lux')}
                  </div>
                  {normReqs.lux && (
                    <div className="text-[10px] text-muted-foreground">
                      {t(lang, 'wymagane', 'required')}: {normReqs.lux}
                    </div>
                  )}
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <Eye className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                  <div className="text-lg font-bold">
                    {calculation.ugr || normReqs.ugr || '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">UGR</div>
                  {normReqs.ugr && (
                    <div className="text-[10px] text-muted-foreground">
                      max: {normReqs.ugr}
                    </div>
                  )}
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <Palette className="w-5 h-5 mx-auto mb-1 text-pink-500" />
                  <div className="text-lg font-bold">
                    {calculation.cri || normReqs.cri_min || '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">CRI</div>
                  {normReqs.cri_min && (
                    <div className="text-[10px] text-muted-foreground">
                      min: {normReqs.cri_min}
                    </div>
                  )}
                </div>
                <div className="text-center p-3 rounded-lg bg-muted/30">
                  <Zap className="w-5 h-5 mx-auto mb-1 text-green-500" />
                  <div className="text-lg font-bold">
                    {calculation.watts_per_m2?.toFixed(1) || '—'}
                  </div>
                  <div className="text-xs text-muted-foreground">W/m²</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                {t(lang, 'Rekomendowane oprawy', 'Recommended Luminaires')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(recommendations.length > 0 ? recommendations : (firstRoom.luminaires_used || [])).map(function(lum, idx) {
                return (
                  <div key={idx} className="flex items-start gap-4 p-3 rounded-lg border border-border mb-3 last:mb-0">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{lum.name || lum.product_name || 'Oprawa'}</div>
                      <div className="text-xs text-muted-foreground">{lum.manufacturer || ''}</div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {lum.power_w && <Badge variant="outline" className="text-[10px]">{lum.power_w}W</Badge>}
                        {lum.lumens && <Badge variant="outline" className="text-[10px]">{lum.lumens} lm</Badge>}
                        {lum.cri && <Badge variant="outline" className="text-[10px]">CRI {lum.cri}</Badge>}
                        {lum.cct_k && <Badge variant="outline" className="text-[10px]">{lum.cct_k}K</Badge>}
                        {lum.quantity && <Badge className="text-[10px] bg-amber-500/10 text-amber-500">{t(lang, 'Szt', 'Qty')}: {lum.quantity}</Badge>}
                      </div>
                    </div>
                    {lum.unit_price > 0 && (
                      <div className="text-right flex-shrink-0">
                        <div className="font-bold text-sm">{(lum.unit_price * (lum.quantity || 1)).toLocaleString()} PLN</div>
                        <div className="text-[10px] text-muted-foreground">{lum.unit_price} PLN/{t(lang, 'szt', 'pc')}</div>
                      </div>
                    )}
                  </div>
                );
              })}
              {recommendations.length === 0 && (!firstRoom.luminaires_used || firstRoom.luminaires_used.length === 0) && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  {t(lang, 'Brak rekomendacji — sprawdz baze produktow', 'No recommendations — check product database')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Explanation */}
          {(result?.explanation || result?.analysis) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">{t(lang, 'Uzasadnienie AI', 'AI Explanation')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {result.explanation || result.analysis}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Cost Summary + Downloads */}
        <div className="space-y-4">
          {/* Cost */}
          {result?.total_cost > 0 && (
            <Card className="border-green-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-500" />
                  {t(lang, 'Koszt calkowity', 'Total Cost')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-500">
                  {result.total_cost.toLocaleString()} PLN
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {(result.total_cost / area).toFixed(0)} PLN/m²
                </div>
              </CardContent>
            </Card>
          )}

          {/* Downloads */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{t(lang, 'Pliki do pobrania', 'Downloads')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={handleDownloadPdf} disabled={downloading === 'pdf'}>
                {downloading === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2 text-red-500" />}
                {t(lang, 'Raport PDF z obliczeniami', 'PDF Report with calculations')}
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={handleDownloadStf} disabled={downloading === 'stf'}>
                {downloading === 'stf' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2 text-blue-500" />}
                {t(lang, 'Projekt DIALux (.stf)', 'DIALux Project (.stf)')}
              </Button>
            </CardContent>
          </Card>

          {/* Norm Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Info className="w-4 h-4" />
                {t(lang, 'Norma PN-EN 12464-1', 'Standard PN-EN 12464-1')}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-1">
              <p>{t(lang, 'Obliczenia metoda strumieniowa (lumen method).', 'Calculations using lumen method.')}</p>
              <p>{t(lang, 'Dokladnosc: ±15-20% dla prostokatnych pomieszczen.', 'Accuracy: ±15-20% for rectangular rooms.')}</p>
              <p>{t(lang, 'Weryfikacja w DIALux zalecana przed realizacja.', 'DIALux verification recommended before implementation.')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
